/**
 * The workspace tool a name refers to, whatever the caller decorated it with.
 *
 * The same tool reaches the server and the client under three spellings. The
 * workspace registers it bare (`plugins_execute_action`). eve qualifies a
 * connection's tools with the connection's namespace and a double underscore
 * (`qualifiedConnectionToolName` → `workspace__plugins_execute_action`), and
 * that qualified name is what an approval policy, a parked tool-approval
 * request and a timeline tool row all carry. A harness may further suffix a
 * compound approval key with `:<discriminator>`. A predicate that compared
 * the raw string matched only the first spelling — so the approval prompt
 * never fired for the other two, and the chat timeline drew a wrench with raw
 * arguments instead of the tool's own presentation.
 *
 * Tool names use single underscores, so the last `__` is always the namespace
 * boundary.
 *
 * @module toolName
 */
export const bareToolName = (name: string): string => {
  const colon = name.indexOf(":");
  let bare = colon > 0 ? name.slice(0, colon) : name;
  const namespaced = bare.lastIndexOf("__");
  if (namespaced > 0) bare = bare.slice(namespaced + 2);
  const slashed = bare.lastIndexOf("/");
  if (slashed >= 0) bare = bare.slice(slashed + 1);
  return bare;
};

/**
 * The connection namespace eve qualified a tool name with (`workspace` for
 * `mcp/workspace__plugins_execute_action:gmail`), or null for a bare name.
 */
export const toolNamespace = (name: string): string | null => {
  const colon = name.indexOf(":");
  const undecorated = colon > 0 ? name.slice(0, colon) : name;
  const namespaced = undecorated.lastIndexOf("__");
  if (namespaced <= 0) return null;
  const qualifier = undecorated.slice(0, namespaced);
  return qualifier.slice(qualifier.lastIndexOf("/") + 1) || null;
};
